import {Component, h} from 'preact'
import {HomepageElement} from '../homepage'
import {Hero} from '../hero'
import TagCloud from '../tag-cloud'
import {HTMLWidget} from '../html-widget/HTMLWidget'
import {KnowledgeBaseHomepageManager} from '../knowledge-base/control/KnowledgeBaseHomepageManager'
import {QuickLinksManager} from '../quick-links/QuickLinksManager'
import {TabsConfigurator} from '../homepage-tabs/TabsConfigurator'
import {FeaturedTopics} from '../featured-topics/FeaturedTopics'
import {BadgesConfigurator} from '../widget-badges/configurator'
import {SolvedTopicsConfigurator} from '../widget-solved-topics/configurator'
import {MostLikedConfigurator} from '../widget-most-liked/configurator'
import CommunityStatisticsConfigurator from '../widget-community-statistics/configurator'
import Portal from 'preact-portal'
import TextButton from '../ui-components/TextButton'
import Icon from '../assets'
import Button from '../ui-components/Button'
import {HomepageContentCurationManager} from '../content-curation/HomepageContentCurationManager'

type TypeConfigElement = Pick<HomepageElement, 'type' | 'config' | 'title'>

interface Props {
  homepageElement: HomepageElement
  onChange: (config: TypeConfigElement) => void
  onClose: () => void
  widgetTitle: string
}

interface State {
  edit: boolean
}

export default class WidgetConfigurator extends Component<Props, State> {
  newConfig: TypeConfigElement

  constructor (props: Props) {
    super()
    this.newConfig = {
      type: props.homepageElement.type,
      config: props.homepageElement.config,
    }
  }

  async done () {
    this.props.onChange(this.newConfig)
  }

  getComponent (): JSX.Element | null {
    const el = this.props.homepageElement
    switch (el.type) {
      case 'hero_banner':
        return <Hero onChange={(config) => {
            this.newConfig = {
              type: el.type,
              config: {
                heroImage: config.hero_image,
                headline: config.headline
              }
            }
          }} heroImage={el.config.heroImage} headline={el.config.headline} />
      case 'html_widget':
        return <HTMLWidget
          title={el.title}
          content={el.config.html}
          deleteButtonAction={() => {}}
          onChange={({ html, title }) => {
            this.newConfig = {
              config: {
                html: html
              },
              type: el.type,
              title: title
            }
          }}
          hideDeleteButton />
      case 'knowledge_base':
        return <KnowledgeBaseHomepageManager
          title={el.title}
          skipLoading
          onChange={config => {
            this.newConfig = {
              type: el.type,
              title: config.title,
              config: {title: config.title}
            }
          }}
        />
      case 'quick_links':
        return <QuickLinksManager
          isActive
          onChange={config => {
            this.newConfig = {
              type: el.type,
              title: config.title,
              config: {
                quickLinks: config.quickLinks.map(link => ({
                  ...link,
                  iconSrc: link.iconSrc,
                  imageSrc: link.imageSrc,
                  openNewTab: link.openNewTab
                })),
                enableBorder: config.settings.enableBorder,
                borderRadius: config.settings.borderRadius
              }
            }
          }}
          title={el.title}
          quickLinks={el.config.quickLinks.map(link => ({
            ...link,
            icon_src: link.iconSrc,
            image_src: link.imageSrc,
            target: link.openNewTab
          }))}
          settings={{
            borderRadius: el.config.borderRadius,
            enableBorder: el.config.enableBorder,
          }}
        />
      case 'category_and_topics_stream':
      case 'category_and_topics_stream_only':
        return <TabsConfigurator
          tabs={el.config.tabs}
          class='tabs-configurator--wide'
          onChange={config => {
            this.newConfig = {
              type: el.type,
              config: { tabs: config.tabs }
            }
          }}
        />
      case 'recommendations':
        return <HomepageContentCurationManager
          curatedTopics={el.config.curatedTopics}
          title={el.title}
          onChange={config => {
            this.newConfig = {
              type: el.type,
              title: config.title,
              config: { curatedTopics: config.curatedTopics }
            }
          }}
        />
      case 'featured_topics':
        return <FeaturedTopics
          title={el.title}
          content={el.config}
          onChange={config => {
            this.newConfig = {
              type: el.type,
              title: config.title,
              config: {
                topics: config.topics,
                firstTopicsNumber: config.firstTopicsNumber,
                buttonLabel: config.buttonLabel,
                buttonURL: config.buttonURL
              }
            }
          }}
        />
      case 'tag_cloud':
        return <TagCloud
          title={el.title}
          amount={el.config.amount}
          onChange={config => {
            this.newConfig = {
              type: el.type,
              title: config.title,
              config: {
                amount: config.amount
              }
            }
          }}
        />
      case 'badges':
        return <BadgesConfigurator
          title={el.title}
          amount={el.config.amount}
          onChange={config => {
            this.newConfig = {
              type: el.type,
              title: config.title,
              config: { amount: config.amount }
            }
          }}
        />
      case 'solved_topics':
        return <SolvedTopicsConfigurator
          title={el.title}
          amount={el.config.amount}
          onChange={config => {
            this.newConfig = {
              type: el.type,
              title: config.title,
              config: { amount: config.amount }
            }
          }}
        />
      case 'most_liked':
        return <MostLikedConfigurator
          title={el.title}
          amount={el.config.amount}
          period={el.config.period}
          onChange={config => {
            this.newConfig = {
              type: el.type,
              title: config.title,
              config: {
                amount: config.amount,
                period: config.period
               }
            }
          }}
        />
      case 'community_statistics':
        return <CommunityStatisticsConfigurator
          title={el.title}
          showTotalTopics={el.config.total_topics}
          showTotalComments={el.config.total_comments}
          showTotalUsers={el.config.total_users}
          onChange={config => {
            this.newConfig = {
              type: el.type,
              title: config.title,
              config: {
                total_topics: config.showTotalTopics,
                total_comments: config.showTotalComments,
                total_users: config.showTotalUsers
              }
            }
          }}
        />
      default:
        return null
    }
  }

  render ({ widgetTitle }: Props) {
    return <Portal into='body'>
      <div class='homepage-widget-configurator'>
        <div class='homepage-widget-configurator--header'>
          <h4  class='homepage-widget-configurator--header-title'>{widgetTitle}</h4>
          <TextButton style={{ padding: '4px', display: 'flex', borderRadius: '2px' }} class='close-btn ' onClick={() => this.props.onClose()}>
            <Icon icon='close' />
          </TextButton>
        </div>
        <div class='box__pad homepage-widget-configurator--body homepage-widget-configurator__controls'>
          {this.getComponent()}
        </div>
        <div class='homepage-widget-configurator--footer'>
          <Button insided onClick={() => this.done()}>
            Done
          </Button>
        </div>
      </div>
    </Portal>
  }
}
